/**
 * The script: which tokens exist, what happens to them, and when. Every time
 * in the animation comes from here, so the renderer can draw any moment as a
 * pure function of `t` (which is what makes scrubbing work).
 */
import { envelope, hexToRgb, type RGB } from "./fx";

/** The architectures the viewer can switch between; they share one timeline. */
export type ModelKind = "dense" | "moe" | "linear";

export type Token = { text: string; id: number; color: RGB; generated: boolean };

const token = (text: string, id: number, hex: string, generated = false): Token => ({
  text,
  id,
  color: hexToRgb(hex),
  generated
});

// Token IDs are real: GPT-4's tokenizer (cl100k_base) on "The Eiffel Tower is in Paris, France."
export const TOKENS: Token[] = [
  token("The", 791, "#4cc9f0"),
  token(" E", 469, "#4895ef"),
  token("iff", 3168, "#7b6cff"),
  token("el", 301, "#b15cff"),
  token(" Tower", 22703, "#f15bb5"),
  token(" is", 374, "#2ee6c5"),
  token(" in", 304, "#9ef01a"),
  token(" Paris", 12366, "#ffd23f", true),
  token(",", 11, "#fff07a", true),
  token(" France", 9822, "#ffc857", true),
  token(".", 13, "#ffe7a0", true)
];
export const PROMPT = "The Eiffel Tower is in";
export const PROMPT_COUNT = 7;
export const VOCAB_SIZE = 100_277;
export const LAYER_COUNT = 90;

// Overview stations, left to right.
export const ST_EMBED = 0;
export const ST_L1 = 1;
export const ST_L2 = 2;
export const ST_L3 = 3;
export const ST_WARP = 4;
export const ST_L90 = 5;
export const ST_LM = 6;
export const LAYER_STATIONS = [ST_L1, ST_L2, ST_L3, ST_WARP, ST_L90];

// Illustrative next-token candidates for each pass; the first one wins.
export const CANDIDATES: string[][] = [
  [" Paris", " France", " the", " Europe", " central"],
  [",", ".", " France", " and", " on"],
  [" France", " the", " which", " near", " a"],
  [".", ",", " and", "!", " with"]
];
export const CANDIDATE_WEIGHTS = [1, 0.34, 0.2, 0.12, 0.07];

// Illustrative routing inside layer 1: two of eight experts per token.
export const EXPERT_COUNT = 8;
export const ROUTES: Array<[number, number]> = [
  [0, 3],
  [5, 1],
  [5, 6],
  [2, 6],
  [7, 3],
  [0, 4],
  [2, 7]
];

// Illustrative attention: word pieces find each other, " in" leans on "Eiffel Tower".
const STRONG: Record<number, Record<number, number>> = {
  2: { 1: 1 },
  3: { 1: 0.85, 2: 1 },
  4: { 1: 0.75, 2: 0.7, 3: 1 },
  5: { 4: 0.8 },
  6: { 4: 1, 3: 0.65, 2: 0.55, 1: 0.5, 5: 0.3 }
};
export const attentionWeight = (target: number, source: number) =>
  STRONG[target]?.[source] ?? 0.22 * Math.pow(0.72, target - source - 1);

// Base durations in seconds at 1x; a fast-forwarded pass divides them by its speed.
const PIPE_TIME = 0.45;
const TRAVEL = 0.42;
const STAGGER = 0.035;
const MEM_DUR = 0.8;
const PREFILL_COMPUTE = 1.7;
const WARP_COMPUTE = 2.0;
const PACKET_RATE = 22;

export type KvWrite = { token: number; leave: number };
export type KvRead = { token: number; leave: number };

export type Run = {
  station: number;
  arrive: number;
  leave: number;
  /** Weight packets: the time each one leaves memory. */
  packets: number[];
  compute: { start: number; end: number; heat: number } | null;
  kvWrites: KvWrite[];
  kvReads: KvRead[];
  /** Embedding lookups: token, time the ID reaches the box. */
  lookups: Array<{ token: number; at: number }>;
};

export type Interior = {
  Z: number;
  zoomIn: [number, number];
  enter: number;
  attnPackets: number[];
  attnHeat: [number, number];
  kvLeave: number[];
  kvTravel: number;
  mixLaunch: number[];
  mixTravel: number;
  mixHeat: [number, number];
  /** Linear attention: when each token folds itself into the fixed-size state. */
  foldLaunch: number[];
  foldTravel: number;
  readTravel: number;
  /** Linear attention: the finished state is stored to memory once. */
  stateStore: number;
  toRouter: number;
  expertPackets: number[];
  dispatch: number[];
  dispatchTravel: number;
  expertOut: number[];
  returnTravel: number;
  toMerge: number;
  exit: number;
  zoomOut: [number, number];
  end: number;
};

export type Pass = {
  index: number;
  kind: "prefill" | "decode";
  tokens: number[];
  out: number;
  speed: number;
  pipeTime: number;
  dropStart: number[];
  dropDur: number;
  travel: number;
  stagger: number;
  runs: Run[];
  /** Prefill only: earlier tokens stop at the last layer; only the final one goes on. */
  retire: number;
  burst: number;
  scoresIn: number;
  converge: number;
  smash: number;
  pop: number;
  land: number;
  end: number;
  interior: Interior | null;
};

export type Caption = { t: number; chapter: string; text: string };

const range = (n: number) => Array.from({ length: n }, (_, i) => i);

const uniformPackets = (start: number, end: number, rate: number) => {
  const n = Math.max(1, Math.round((end - start) * rate));
  return range(n).map((k) => start + (k / n) * (end - start));
};

/** Bursty stream standing in for 86 layers each loading their own weights. */
const burstPackets = (start: number, end: number, rate: number, bursts: number) => {
  const out: number[] = [];
  const slot = (end - start) / bursts;
  for (let b = 0; b < bursts; b++) out.push(...uniformPackets(start + b * slot, start + b * slot + slot * 0.5, rate));
  return out;
};

function buildInterior(Z: number): Interior {
  const n = PROMPT_COUNT;
  const kvLeave = range(n).map((i) => Z + 2.9 + i * 0.17);
  const mixLaunch = range(n).map((i) => (i === 0 ? -1 : Z + 4.9 + (i - 1) * 0.38));
  const dispatch = range(n).map((i) => Z + 9.0 + i * 0.12);
  const expertOut = range(n).map((i) => Z + 10.7 + i * 0.12);
  return {
    Z,
    zoomIn: [Z, Z + 1.1],
    enter: Z + 1.1,
    attnPackets: uniformPackets(Z + 1.5, Z + 2.7, PACKET_RATE),
    attnHeat: [Z + 2.2, Z + 4.5],
    kvLeave,
    kvTravel: 0.75,
    mixLaunch,
    mixTravel: 0.75,
    mixHeat: [Z + 4.8, Z + 7.7],
    foldLaunch: range(n).map((i) => Z + 4.8 + i * 0.36),
    foldTravel: 0.45,
    readTravel: 0.4,
    stateStore: Z + 7.5,
    toRouter: Z + 8.0,
    expertPackets: uniformPackets(Z + 8.3, Z + 9.3, PACKET_RATE),
    dispatch,
    dispatchTravel: 0.55,
    expertOut,
    returnTravel: 0.6,
    toMerge: Z + 9.3,
    exit: Z + 12.3,
    zoomOut: [Z + 12.9, Z + 14.0],
    end: Z + 14.0
  };
}

function buildPass(
  index: number,
  kind: Pass["kind"],
  tokens: number[],
  out: number,
  start: number,
  speed: number,
  hold: number
): Pass {
  const s = (x: number) => x / speed;
  const n = tokens.length;
  const pipeTime = s(PIPE_TIME);
  const dropDur = s(kind === "prefill" ? 0.75 : 0.9);
  const dropStart = tokens.map((_, k) => start + k * s(0.12));
  const travel = s(TRAVEL);
  const stagger = n > 1 ? s(STAGGER) : 0;
  const runs: Run[] = [];
  let interior: Interior | null = null;

  // Embedding: each ID triggers its own lookup when it lands.
  const lookups = tokens.map((token, k) => ({ token, at: dropStart[k] + dropDur }));
  const lastLookup = lookups[lookups.length - 1].at;
  const embedArrive = lastLookup + pipeTime + s(0.15);
  runs.push({
    station: ST_EMBED,
    arrive: embedArrive,
    leave: embedArrive + s(0.2),
    packets: lookups.flatMap((l) => [l.at, l.at + s(0.05), l.at + s(0.1)]),
    compute: null,
    kvWrites: [],
    kvReads: [],
    lookups
  });

  let leave = runs[0].leave;
  for (const station of [...LAYER_STATIONS, ST_LM]) {
    const prevLeave = leave;
    const movers = station === ST_LM ? 1 : n;
    const arrive = leave + travel + (movers > 1 ? stagger * (movers - 1) : 0);
    const run: Run = {
      station,
      arrive,
      leave: arrive,
      packets: [],
      compute: null,
      kvWrites: [],
      kvReads: [],
      lookups: []
    };

    if (station === ST_L1 && kind === "prefill") {
      interior = buildInterior(arrive + 0.15);
      run.leave = interior.end;
      run.compute = { start: interior.attnHeat[0], end: interior.zoomOut[0] + 0.3, heat: 0.9 };
      // KV rows land in layer 1's cache exactly when the zoomed-in view shows them landing.
      run.kvWrites = tokens.map((token, k) => ({ token, leave: interior.kvLeave[k] + interior.kvTravel - pipeTime }));
    } else if (station === ST_LM) {
      run.packets = uniformPackets(arrive, arrive + s(MEM_DUR), PACKET_RATE * speed);
      run.compute = { start: arrive + pipeTime, end: arrive + s(MEM_DUR) + pipeTime, heat: 0.3 };
      run.leave = arrive + s(MEM_DUR) + pipeTime + s(0.05);
    } else if (kind === "prefill") {
      const dur = station === ST_WARP ? WARP_COMPUTE : PREFILL_COMPUTE;
      const memStart = arrive - 0.35;
      run.packets =
        station === ST_WARP
          ? burstPackets(memStart, memStart + dur, PACKET_RATE * 1.4, 7)
          : uniformPackets(memStart, memStart + MEM_DUR, PACKET_RATE);
      run.compute = { start: arrive, end: arrive + dur, heat: 1 };
      run.kvWrites = tokens.map((token, k) => ({ token, leave: arrive + 0.35 + k * 0.12 }));
      run.leave = arrive + dur;
    } else {
      // Decode: one token, so almost no math, but the same weights still have to come in.
      // They start streaming the moment the token leaves the previous layer, so memory never rests.
      const memDur = s(station === ST_WARP ? MEM_DUR * 1.25 : MEM_DUR);
      const memStart = prevLeave;
      run.packets = uniformPackets(memStart, memStart + memDur, PACKET_RATE * speed);
      run.compute = { start: arrive, end: memStart + memDur + pipeTime, heat: 0.12 };
      const cached = tokens[0];
      run.kvReads = range(cached).map((token) => ({
        token,
        leave: memStart + (token / Math.max(1, cached)) * memDur * 0.85
      }));
      run.kvWrites = [{ token: tokens[0], leave: memStart + memDur + pipeTime - s(0.05) }];
      run.leave = Math.max(arrive + s(0.3), memStart + memDur + pipeTime + s(0.12));
    }
    runs.push(run);
    leave = run.leave;
  }

  const retire = runs.find((r) => r.station === ST_L90).leave;
  const burst = leave;
  const scoresIn = burst + s(0.85);
  const converge = scoresIn + s(hold);
  const smash = converge + s(0.5);
  const pop = smash + s(0.2);
  const land = pop + s(0.25) + s(0.8);
  return {
    index,
    kind,
    tokens,
    out,
    speed,
    pipeTime,
    dropStart,
    dropDur,
    travel,
    stagger,
    runs,
    retire,
    burst,
    scoresIn,
    converge,
    smash,
    pop,
    land,
    end: land + s(0.3),
    interior
  };
}

export type Timeline = {
  typeStart: number;
  typeEnd: number;
  tokenizeStart: number;
  splitStart: number;
  idStart: number;
  prefillBanner: number;
  decodeBanner: number;
  passes: Pass[];
  endCard: number;
  duration: number;
  captions: Record<ModelKind, Caption[]>;
  chapters: Array<{ t: number; label: string }>;
};

export function buildTimeline(): Timeline {
  const typeStart = 0.5;
  const typeEnd = typeStart + PROMPT.length / 14;
  const tokenizeStart = typeEnd + 0.5;
  const splitStart = tokenizeStart + 0.7;
  const idStart = splitStart + 0.6;
  const prefillBanner = idStart + 1.4;

  const passes: Pass[] = [];
  const prefill = buildPass(0, "prefill", range(PROMPT_COUNT), PROMPT_COUNT, prefillBanner + 0.9, 1, 1.9);
  passes.push(prefill);

  const decodeBanner = prefill.end + 0.2;
  let start = decodeBanner + 0.9;
  const decodeSpeeds = [1, 2.6, 3];
  const decodeHolds = [0.8, 0.25, 0.25];
  for (let k = 0; k < decodeSpeeds.length; k++) {
    const tokenIndex = PROMPT_COUNT + k;
    const pass = buildPass(k + 1, "decode", [tokenIndex], tokenIndex + 1, start, decodeSpeeds[k], decodeHolds[k]);
    passes.push(pass);
    start = pass.land + 0.35;
  }
  const last = passes[passes.length - 1];
  const endCard = last.end + 0.4;
  const duration = endCard + 7;

  const inner = prefill.interior;
  const lm = (p: Pass) => p.runs.find((r) => r.station === ST_LM);
  const l1 = (p: Pass) => p.runs.find((r) => r.station === ST_L1);
  const warp = prefill.runs.find((r) => r.station === ST_WARP);

  const captionsFor = (model: ModelKind): Caption[] => {
    const linear = model === "linear";
    return [
      { t: 0, chapter: "Text", text: "It starts as plain text." },
      {
        t: tokenizeStart,
        chapter: "Tokenize",
        text: "The tokenizer chops text into tokens. Common words stay whole; rarer ones like “Eiffel” split into pieces. Each token is just an ID number."
      },
      {
        t: prefillBanner,
        chapter: "Embed",
        text: "Each ID fetches its row from the embedding table: a long list of numbers. No math yet, just a memory lookup."
      },
      {
        t: inner.Z,
        chapter: "Inside a layer",
        text: "Zooming into one layer. The whole prompt goes through it at once."
      },
      {
        t: inner.attnPackets[0],
        chapter: "Inside a layer",
        text: linear
          ? "The layer’s weights stream in from memory and every token is multiplied against them. Heavy math, so the box heats up."
          : "The layer’s weights stream in from memory and every token is multiplied against them. Heavy math, so the box heats up. Each token also leaves a key and value in the KV cache."
      },
      {
        t: inner.mixHeat[0],
        chapter: "Inside a layer",
        text: linear
          ? "Linear attention: each token folds itself into a fixed-size state and reads the blend back out. Old tokens fade as new ones arrive, so the state never grows."
          : "Attention: each token looks back at earlier tokens and blends what it finds into itself. Many vectors in, one out."
      },
      {
        t: inner.toRouter,
        chapter: "Inside a layer",
        text:
          model === "moe"
            ? "A router sends each token to a few specialist sub-networks, called experts, and blends their answers back into one vector. Most of the model’s weights live in the experts."
            : "The feed-forward block blows each vector up to several times its width, then squeezes it back down. Most of the model’s weights live here."
      },
      {
        t: inner.zoomOut[0],
        chapter: "Prefill",
        text: "Same again, layer after layer; a large model has around ninety. This first pass is called prefill, and it keeps the math units busy."
      },
      {
        t: warp.arrive - 0.4,
        chapter: "Prefill",
        text: "Real prompts often run thousands of tokens, which is what makes prefill so math-heavy. This one is short so you can follow it."
      },
      {
        t: lm(prefill).arrive - 0.3,
        chapter: "Pick a token",
        text: "Only the last token’s vector predicts what comes next. It becomes a score for every token in the vocabulary, about 100,000 here."
      },
      {
        t: prefill.converge,
        chapter: "Pick a token",
        text: "One token wins. A whole pass through the model, squeezed into a single token."
      },
      {
        t: decodeBanner,
        chapter: "Decode",
        text: linear
          ? "Now the loop. The new token goes back in alone; what the model kept from earlier tokens lives in each layer’s state."
          : "Now the loop. The new token goes back in alone; earlier tokens are already waiting in the KV cache."
      },
      {
        t: l1(passes[1]).arrive - 0.2,
        chapter: "Decode",
        text: linear
          ? "One token means little math, but every layer still reads its weights from memory. The state it reads is small, yet the math units still mostly wait."
          : `One token means little math, but every layer still reads ${model === "moe" ? "the weights it uses" : "all its weights"} from memory, plus the whole KV cache. The math units mostly wait.`
      },
      {
        t: passes[2].dropStart[0],
        chapter: "Decode",
        text: linear
          ? "Every new token repeats the trip and re-reads the weights. The state stays the same size, however long the text gets."
          : "Every new token repeats the trip and re-reads the weights. The KV cache grows by one token each time."
      },
      {
        t: endCard,
        chapter: "Takeaway",
        text: linear
          ? "Prefill is limited by math, decode by memory. A fixed state stops memory traffic from growing with the text, but the weights still come in for every token."
          : "Prefill is limited by math, decode by memory. That’s why providers batch many users together: one trip through memory serves everyone’s next token."
      }
    ];
  };
  const captions: Record<ModelKind, Caption[]> = {
    dense: captionsFor("dense"),
    moe: captionsFor("moe"),
    linear: captionsFor("linear")
  };

  const chapters: Timeline["chapters"] = [];
  for (const c of captions.moe)
    if (!chapters.length || chapters[chapters.length - 1].label !== c.chapter)
      chapters.push({ t: c.t, label: c.chapter });

  return {
    typeStart,
    typeEnd,
    tokenizeStart,
    splitStart,
    idStart,
    prefillBanner,
    decodeBanner,
    passes,
    endCard,
    duration,
    captions,
    chapters
  };
}

/** How busy the math units (first) and the memory pipes (second) are at `t`, each 0..1. */
export function loadAt(tl: Timeline, t: number): [number, number] {
  let c = 0;
  let m = 0;
  for (const pass of tl.passes) {
    const embed = pass.runs[0];
    if (t >= embed.lookups[0].at && t < embed.arrive) m = Math.max(m, 0.3);
    const I = pass.interior;
    if (I && t >= I.Z && t < I.end) {
      const attn = Math.max(envelope(t, I.attnHeat[0], I.attnHeat[1]), 0.5 * envelope(t, I.mixHeat[0], I.mixHeat[1]));
      const experts = envelope(t, I.dispatch[0] + I.dispatchTravel, I.expertOut[PROMPT_COUNT - 1], 0.2, 0.3) * 0.9;
      c = Math.max(c, attn, experts);
      const reading = [...I.attnPackets, ...I.expertPackets].some((e) => t >= e && t <= e + 0.35);
      m = Math.max(m, reading ? 0.55 : 0.1);
      continue;
    }
    let prevLeave = embed.leave;
    for (const run of pass.runs.slice(1)) {
      const inWindow = t >= prevLeave && t < run.leave;
      prevLeave = run.leave;
      if (!inWindow) continue;
      if (run.station === ST_LM) {
        c = Math.max(c, 0.3);
        m = Math.max(m, 0.9);
      } else if (pass.kind === "prefill") {
        c = Math.max(c, 1);
        m = Math.max(m, 0.5);
      } else {
        c = Math.max(c, 0.12);
        m = Math.max(m, 1);
      }
    }
    if (t >= pass.burst && t < pass.smash) c = Math.max(c, 0.08);
  }
  return [c, m];
}
